import { useEffect, useState } from 'react';
import { Page, Card, DataTable, Filters, Button, Modal, Form, FormLayout, TextField, Checkbox, Badge, InlineGrid, Select } from '@shopify/polaris';
import { getTableFooter, fetchTableData, postRequest, putRequest, formatToCurrency } from '../../shared/components/functions.jsx';
import { applyFilters, addFilters, handleFilterChange, removeFilter, handleFiltersClearAll } from '../../shared/components/listFilter.jsx';
import { useUrl } from '../../shared/util/hanldesave';
import { useDebounce } from '../../shared/components/inputs/useDebounce.js';
import { useLookupStore } from '../../context/useStore.js';
import { useNavigate } from 'react-router-dom';

export default function FleetPage() {
  const url = useUrl();
  const navigate = useNavigate();
  const users = useLookupStore((state) => state.users);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [filterLoading, setFilterLoading] = useState(false);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  const [filters, setFilters] = useState({
    q: { type: 'text', name: 'Q', value: url.get('q') || '' },
    brand: { type: 'text', name: 'Brand', value: url.get('brand') || '' },
    model: { type: 'text', name: 'Model', value: url.get('model') || '' },
    plate: { type: 'text', name: 'License Plate', value: url.get('plate') || '' },
    fuel: { type: 'text', name: 'Fuel', value: url.get('fuel') || '' },
    status: { type: 'status', name: 'Status', value: url.get('status') || 'all' }
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const [vehicle, setVehicle] = useState({
    Merk: '',
    Model: '',
    Nummerplaat: '',
    DatumStart: '',
    Discontinued: false,
    ChasisNummer: '',
    FiscalePK: '',
    EuroNorm: '',
    Brandstof: '',
    CO2GramPerKm: '',
    CatalogusPrijs: '',
    KmStand: '',
    GebruikerID: ''
  });

  const fetchData = async (abortController) => await fetchTableData(page, `/api/voertuigen?sort=${sortField}&direction=${sortDirection}`, setItems, filters, abortController, setFilterLoading);
  const debouncedFetchData = useDebounce(fetchData);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchDataWithAbort = async () => debouncedFetchData(abortController);
    fetchDataWithAbort();
    return () => abortController.abort();
  }, [page, sortField, sortDirection, filters]);

  const resetForm = () => {
    setVehicle({
      Merk: '',
      Model: '',
      Nummerplaat: '',
      DatumStart: '',
      Discontinued: false,
      ChasisNummer: '',
      FiscalePK: '',
      EuroNorm: '',
      Brandstof: '',
      CO2GramPerKm: '',
      CatalogusPrijs: '',
      KmStand: '',
      GebruikerID: ''
    });
    setEditId(null);
    setEditing(false);
  };

  const openAdd = () => {
    resetForm();
    setEditing(false);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setVehicle({
      Merk: item.Merk || '',
      Model: item.Model || '',
      Nummerplaat: item.Nummerplaat || '',
      DatumStart: item.DatumStart ? String(item.DatumStart).substring(0, 10) : '',
      Discontinued: !!item.Discontinued,
      ChasisNummer: item.ChasisNummer || '',
      FiscalePK: item.FiscalePK || '',
      EuroNorm: item.EuroNorm || '',
      Brandstof: item.Brandstof || '',
      CO2GramPerKm: item.CO2GramPerKm ?? '',
      CatalogusPrijs: item.CatalogusPrijs ?? '',
      KmStand: item.KmStand ?? '',
      GebruikerID: item.GebruikerID != null ? String(item.GebruikerID) : ''
    });
    setEditId(item.id);
    setEditing(true);
    setIsModalOpen(true);
  };

  // Helper to get user options for Select
  const userOptions = [
    { label: 'No user assigned', value: '' },
    ...users
      .filter((user) => user.Inactief === 0 || user.Inactief === false || user.Inactief == null)
      .map((user) => ({
        label: user.Gebruikernaam || user.Gebruiker || user.username || user.full_name || `User ${user.GebruikerID}`,
        value: String(user.GebruikerID)
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ];

  const save = async () => {
    const payload = {
      ...vehicle,
      CO2GramPerKm: vehicle.CO2GramPerKm === '' ? null : Number(vehicle.CO2GramPerKm),
      CatalogusPrijs: vehicle.CatalogusPrijs === '' ? null : Number(vehicle.CatalogusPrijs),
      KmStand: vehicle.KmStand === '' ? null : Number(vehicle.KmStand),
      GebruikerID: vehicle.GebruikerID === '' ? null : Number(vehicle.GebruikerID)
    };
    if (editing && editId) {
      const res = await putRequest(`/api/voertuigen/${editId}`, payload);
      if (res) {
        setIsModalOpen(false);
        resetForm();
        const abortController = new AbortController();
        fetchData(abortController);
      }
    } else {
      const res = await postRequest('/api/voertuigen', payload);
      if (res) {
        setIsModalOpen(false);
        resetForm();
        const abortController = new AbortController();
        fetchData(abortController);
      }
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const rows = Array.isArray(items)
    ? items.map((item) => [
        item.id,
        item.Merk || '',
        item.Model || '',
        item.Nummerplaat || '',
        item.DatumStart ? String(item.DatumStart).substring(0, 10) : '',
        item.ChasisNummer || '',
        item.FiscalePK || '',
        item.EuroNorm || '',
        item.Brandstof || '',
        item.CO2GramPerKm ? `${item.CO2GramPerKm} g/km` : '',
        item.CatalogusPrijs ? formatToCurrency(item.CatalogusPrijs) : '',
        item.KmStand ? `${item.KmStand.toLocaleString()} km` : '',
        item.Discontinued ? <Badge tone="critical">Discontinued</Badge> : <Badge tone="success">Active</Badge>,
        item.AssignedUser || '',
        <Button size="micro" variant="plain" onClick={() => openEdit(item)}>
          Edit
        </Button>
      ])
    : [];

  const totals =
    Array.isArray(items) && items.length > 0
      ? [
          '', // ID
          '', // Brand
          '', // Model
          '', // Plate
          '', // Start date
          '', // Chassis
          '', // Fiscale PK
          '', // Euro Norm
          '', // Fuel
          '', // CO2
          formatToCurrency(items.reduce((p, c) => p + (Number(c.CatalogusPrijs) || 0), 0)),
          `${items.reduce((p, c) => p + (Number(c.KmStand) || 0), 0).toLocaleString()} km`,
          '', // Status
          '', // User
          '' // Actions
        ]
      : null;

  return (
    <Page title="Fleet" fullWidth primaryAction={{ content: 'Add vehicle', onAction: openAdd }}>
      <Card>
        <Filters
          queryValue={filters.q.value}
          filters={addFilters(filters, setFilters, navigate, setPage)}
          appliedFilters={applyFilters(filters, setFilters, navigate, setPage)}
          loading={filterLoading}
          onQueryChange={(e) => handleFilterChange(e, filters, setFilters, 'q', navigate, setPage)}
          onQueryClear={() => removeFilter('q', filters, setFilters, navigate, setPage)}
          autoComplete="off"
          queryPlaceholder="Filter vehicles by brand, model, plate, fuel..."
          onClearAll={() => handleFiltersClearAll(filters, setFilters, navigate, setPage)}
        />
        <DataTable
          stickyHeader
          increasedTableDensity
          verticalAlign="middle"
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
          totals={totals}
          showTotalsInFooter
          headings={[
            <Button variant="plain" onClick={() => handleSort('id')}>
              ID {getSortIcon('id')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('Merk')}>
              Brand {getSortIcon('Merk')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('Model')}>
              Model {getSortIcon('Model')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('Nummerplaat')}>
              Plate {getSortIcon('Nummerplaat')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('DatumStart')}>
              Start date {getSortIcon('DatumStart')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('ChasisNummer')}>
              Chassis {getSortIcon('ChasisNummer')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('FiscalePK')}>
              Fiscale PK {getSortIcon('FiscalePK')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('EuroNorm')}>
              Euro Norm {getSortIcon('EuroNorm')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('Brandstof')}>
              Fuel {getSortIcon('Brandstof')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('CO2GramPerKm')}>
              CO2 {getSortIcon('CO2GramPerKm')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('CatalogusPrijs')}>
              Price {getSortIcon('CatalogusPrijs')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('KmStand')}>
              Mileage {getSortIcon('KmStand')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('Discontinued')}>
              Status {getSortIcon('Discontinued')}
            </Button>,
            <Button variant="plain" onClick={() => handleSort('GebruikerID')}>
              User
            </Button>,
            'Actions'
          ]}
          rows={rows}
          footerContent={getTableFooter(rows, page, setPage, 'vehicles')}
        />
      </Card>

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editing ? `Edit vehicle #${editId}` : 'Add vehicle'}
        primaryAction={{ content: editing ? 'Save changes' : 'Add vehicle', onAction: save }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setIsModalOpen(false) }]}
      >
        <Modal.Section>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <FormLayout>
              <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="400">
                <TextField label="Brand (Merk)" value={vehicle.Merk} onChange={(v) => setVehicle({ ...vehicle, Merk: v })} autoComplete="off" />
                <TextField label="Model" value={vehicle.Model} onChange={(v) => setVehicle({ ...vehicle, Model: v })} autoComplete="off" />
                <TextField label="License plate (Nummerplaat)" value={vehicle.Nummerplaat} onChange={(v) => setVehicle({ ...vehicle, Nummerplaat: v })} autoComplete="off" />
                <TextField label="Start date" type="date" value={vehicle.DatumStart || ''} onChange={(v) => setVehicle({ ...vehicle, DatumStart: v })} />
                <TextField label="Chassis number" value={vehicle.ChasisNummer} onChange={(v) => setVehicle({ ...vehicle, ChasisNummer: v })} autoComplete="off" />
                <TextField label="Fiscale PK" value={vehicle.FiscalePK} onChange={(v) => setVehicle({ ...vehicle, FiscalePK: v })} autoComplete="off" />
                <TextField label="Euro norm" value={vehicle.EuroNorm} onChange={(v) => setVehicle({ ...vehicle, EuroNorm: v })} autoComplete="off" />
                <TextField label="Fuel (Brandstof)" value={vehicle.Brandstof} onChange={(v) => setVehicle({ ...vehicle, Brandstof: v })} autoComplete="off" />
                <TextField label="CO2 g/km" type="number" value={String(vehicle.CO2GramPerKm)} onChange={(v) => setVehicle({ ...vehicle, CO2GramPerKm: v })} autoComplete="off" />
                <TextField label="Catalog price" type="number" value={String(vehicle.CatalogusPrijs)} onChange={(v) => setVehicle({ ...vehicle, CatalogusPrijs: v })} autoComplete="off" />
                <TextField label="Mileage (KM)" type="number" value={String(vehicle.KmStand)} onChange={(v) => setVehicle({ ...vehicle, KmStand: v })} autoComplete="off" />
                <Select
                  label="Assigned User"
                  options={userOptions}
                  value={vehicle.GebruikerID != null ? String(vehicle.GebruikerID) : ''}
                  onChange={(value) => setVehicle({ ...vehicle, GebruikerID: value })}
                />
                <Checkbox label="Discontinued" checked={!!vehicle.Discontinued} onChange={(v) => setVehicle({ ...vehicle, Discontinued: v })} />
              </InlineGrid>
            </FormLayout>
          </Form>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
